import { useCallback, useState } from 'react';
import {
  markeerBezocht,
  parseKaartInfo,
  parseStatusMap,
  standaardKaart,
  toggleVlag,
  voegBezoekToe,
  type KaartInfo,
  type StatusMap,
} from '../utils/museum';
import { readStored } from '../utils/storage';
import { usePersistentState } from './usePersistentState';

/** Opslagsleutels. Bij een schemawijziging: nieuwe sleutel + migratie in de parser. */
export const OPSLAG = {
  status: 'mk-status-v1',
  /** v1 had een ingebouwde ingangsdatum (een jaar terug). v2 laat die leeg tot jij hem invult. */
  kaart: 'mk-kaart-v2',
  kaartV1: 'mk-kaart-v1',
  laatsteBackup: 'mk-backup-v1',
  herinneringGezien: 'mk-herinnering-v1',
} as const;

/** Jouw markeringen (bezocht / wensenlijst / favoriet) en je kaartgegevens, veilig in localStorage. */
export function useMuseumStatus() {
  const [status, setStatus] = usePersistentState<StatusMap>(OPSLAG.status, {}, parseStatusMap);
  // Eenmalige migratie: de kaartprijs uit v1 blijft, de (verzonnen) ingangsdatum niet.
  const [beginKaart] = useState<KaartInfo>(() => {
    const oud = readStored<KaartInfo | undefined>(OPSLAG.kaartV1, undefined, parseKaartInfo);
    return oud ? { prijs: oud.prijs, vanaf: null } : standaardKaart();
  });
  const [kaart, setKaart] = usePersistentState<KaartInfo>(OPSLAG.kaart, beginKaart, parseKaartInfo);
  const [laatsteBackup, setLaatsteBackup] = usePersistentState<string | null>(OPSLAG.laatsteBackup, null, (r) =>
    r === null ? null : typeof r === 'string' && /^\d{4}-\d{2}-\d{2}/.test(r) ? r : undefined,
  );
  // Voor welke verloopdatum je de herinnering al hebt weggeklikt.
  const [herinneringGezien, setHerinneringGezien] = usePersistentState<string | null>(OPSLAG.herinneringGezien, null, (r) =>
    r === null ? null : typeof r === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(r) ? r : undefined,
  );

  const wissel = useCallback(
    (id: string, wat: 'b' | 'w' | 'f') => {
      let vorige: StatusMap = {};
      setStatus((s) => {
        vorige = s;
        return wat === 'b' ? markeerBezocht(s, id) : toggleVlag(s, id, wat);
      });
      /** Herstelt de toestand van vóór deze actie (voor "Ongedaan maken"). */
      return () => setStatus(vorige);
    },
    [setStatus],
  );

  const extraBezoek = useCallback((id: string) => setStatus((s) => voegBezoekToe(s, id)), [setStatus]);

  const zetOpWensenlijst = useCallback(
    (ids: string[]) => {
      let vorige: StatusMap = {};
      setStatus((s) => {
        vorige = s;
        const next = { ...s };
        for (const id of ids) next[id] = { ...next[id], w: true };
        return next;
      });
      return () => setStatus(vorige);
    },
    [setStatus],
  );

  return {
    status,
    setStatus,
    kaart,
    setKaart,
    wissel,
    extraBezoek,
    zetOpWensenlijst,
    laatsteBackup,
    setLaatsteBackup,
    herinneringGezien,
    setHerinneringGezien,
  };
}

export type MuseumStatusApi = ReturnType<typeof useMuseumStatus>;
